import { argon2id } from "@noble/hashes/argon2";

const KDF_CONTEXT = "receipt-dashboard-store-credentials";

const toBase64 = (bytes) => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const fromBase64 = (value) => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const deriveEncryptionKey = async (pin, usages) => {
  const encoder = new TextEncoder();
  const pinSaltBytes = encoder.encode(`pin-salt:${pin}:receipt-dashboard`);
  const derivedKeyBytes = argon2id(
    encoder.encode(KDF_CONTEXT),
    pinSaltBytes,
    {
      t: 3,
      m: 19456,
      p: 1,
      dkLen: 32,
    },
  );

  return window.crypto.subtle.importKey(
    "raw",
    derivedKeyBytes,
    { name: "AES-GCM", length: 256 },
    false,
    usages,
  );
};

export const encryptStorePassword = async (password, pin) => {
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveEncryptionKey(pin, ["encrypt"]);
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoder.encode(password),
  );

  return {
    encryptedPassword: toBase64(new Uint8Array(encryptedBuffer)),
    iv: toBase64(iv),
  };
};

export const decryptStorePassword = async (encryptedPassword, iv, pin) => {
  const key = await deriveEncryptionKey(pin, ["decrypt"]);
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromBase64(iv) },
    key,
    fromBase64(encryptedPassword),
  );

  return new TextDecoder().decode(decrypted);
};

/** localStorage key — shared by auth screen, Sidebar, mobile store picker */
export const STORED_MULTI_STORE_CREDENTIALS_KEY =
  "multi-store-credentials-v1";

/**
 * Merge one encrypted credential into persisted multi-store credentials (by email).
 */
export function upsertEncryptedStoreCredential(entry) {
  try {
    const existingRaw = window.localStorage.getItem(
      STORED_MULTI_STORE_CREDENTIALS_KEY,
    );
    const existing = existingRaw ? JSON.parse(existingRaw) : [];
    const withoutCurrent = Array.isArray(existing)
      ? existing.filter((item) => item.email !== entry.email)
      : [];
    const next = [...withoutCurrent, entry];
    window.localStorage.setItem(
      STORED_MULTI_STORE_CREDENTIALS_KEY,
      JSON.stringify(next),
    );
  } catch (error) {
    console.error("Failed to persist store credential:", error);
  }
}

/**
 * Encrypt and save every store row under the same PIN (used after multi-store sign-in
 * so all accounts appear in the switcher even if blur-validation missed a save).
 */
export async function persistMultiStoreCredentialsForPin(stores, pin) {
  if (!pin || !window.crypto?.subtle || !Array.isArray(stores)) return;

  for (const store of stores) {
    const email = store.email?.trim();
    const password = store.password;
    if (!email || !password) continue;

    try {
      const encryptedPayload = await encryptStorePassword(password, pin);
      upsertEncryptedStoreCredential({
        email,
        encryptedPassword: encryptedPayload.encryptedPassword,
        iv: encryptedPayload.iv,
        encryptedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to encrypt/store credential for", email, error);
    }
  }
}
